import clsx from 'clsx';
import { ChevronDown, ChevronUp, Download, FileCode2, Info, ListChecks, Printer, ShieldAlert, Sparkles } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { EFFICIENCY_FAST, EFFICIENCY_GOOD, EFFICIENCY_LOW, PLAN_COVERAGE_LOW, efficiencyTone, isoWeek } from '@pz/core';
import { AreaBars } from '../components/areas';
import { HourTerms, HourTypeBars, coverageLow, useCoverageText, useHourTypesTitle } from '../components/hour-types';
import { Avatar } from '../components/avatar';
import { HBars, WeekBars } from '../components/charts';
import { Button, Checkbox, EmptyState, ErrorBox, Loading, Modal, Notice, PageHeader, Panel, Pill, SortTh, Stat, td, th, useToast } from '../components/ui';
import { HtmlReportHost, ReportHeader, ReportHost } from '../components/report-host';
import { useTableSort } from '../lib/sort';
import type { ProcOutput } from '@pz/server';
import { saveFile } from '../lib/download';
import { d, h, hh, pct } from '../lib/format';
import { useQ } from '../lib/hooks';
import { ScrollTable } from '../components/virtual';
import { useMe } from '../lib/me';
import { rpc } from '../lib/rpc';
import { RangeButtons, useRange } from './Cockpit';
import { EfficiencyTable } from './Overview';

type TeamRow = ProcOutput<'kpi.team'>['rows'][number];
type PersonData = ProcOutput<'kpi.person'>;
/** A report of the staff review (PDF or interactive HTML) for the chosen persons (round 5, request 17). */
interface ReportJob {
  format: 'pdf' | 'html';
  rows: TeamRow[];
  details: PersonData[];
}
/** Persons with their details in one report (each detail is one more request and one more line in the audit log). */
const REPORT_DETAILS = 15;

/**
 * The figures on this page, one entry per criterion with the table columns it fills (1-based, 1 = person). Nothing is
 * measured that is not listed here; the panel states what each criterion means so the evaluation can be explained.
 */
const CRITERIA = [
  { key: 'balance', cols: [2, 3, 4] },
  { key: 'weeks', cols: [5] },
  { key: 'billable', cols: [6] },
  { key: 'projectShare', cols: [7] },
  { key: 'efficiency', cols: [8] },
  { key: 'punctuality', cols: [9] },
  { key: 'corrections', cols: [10] },
] as const;

const HIDDEN_KEY = 'pz-eval-hidden';

function readHidden(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(HIDDEN_KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Mitarbeiterauswertung (staff review): the place where management looks at the personal figures of
 * each employee. Confidential; the page view and every opened person are written to the audit log.
 * "Auslastung" is a different tool: capacity planning (who has time), not an assessment.
 */
export function EvaluationPage() {
  const { t } = useTranslation();
  const { can } = useMe();
  const toast = useToast();
  const coverageText = useCoverageText();
  const hourTypesTitle = useHourTypesTitle();
  const r = useRange('last3m');
  const q = useQ('kpi.team', { from: r.from, to: r.to }, { enabled: can('kpi.team.view') });
  const people = useQ('people.list', {}, { enabled: can('kpi.team.view') });
  const avatars = new Map((people.data?.people ?? []).map((p) => [p.id, p.avatarAt]));
  const [person, setPerson] = useState<{ id: string; name: string } | null>(null);
  // ?person=<id> opens the person at once (section search, round 5 request 32); only people of the evaluation itself
  const [params, setParams] = useSearchParams();
  const wanted = params.get('person');
  useEffect(() => {
    if (!wanted || !q.data) return;
    const row = q.data.rows.find((x) => x.personId === wanted);
    if (row) setPerson({ id: row.personId, name: row.name });
    const next = new URLSearchParams(params);
    next.delete('person');
    setParams(next, { replace: true });
  }, [wanted, q.data, params, setParams]);
  const [exporting, setExporting] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [report, setReport] = useState<ReportJob | null>(null);
  // which criteria this view shows: a choice of the viewer for this device, the figures themselves do not change
  const [criteriaOpen, setCriteriaOpen] = useState(false);
  const [hidden, setHidden] = useState<string[]>(readHidden);
  const toggleCriterion = (key: string) =>
    setHidden((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      try {
        localStorage.setItem(HIDDEN_KEY, JSON.stringify(next));
      } catch {
        /* storage not available: the choice only lasts for this visit */
      }
      return next;
    });
  const showAll = () => {
    setHidden([]);
    try {
      localStorage.removeItem(HIDDEN_KEY);
    } catch {
      /* storage not available */
    }
  };
  const hiddenCols = CRITERIA.filter((c) => hidden.includes(c.key)).flatMap((c) => c.cols);

  // sorting by every column (round 5, request 17); a value that cannot be computed sorts last
  const fin = (x: number) => (Number.isFinite(x) ? x : null);
  const sorting = useTableSort<TeamRow>('evaluation', {
    name: (x) => x.name,
    capacity: (x) => x.capacityMinutes,
    booked: (x) => x.bookedMinutes,
    balance: (x) => x.balanceMinutes,
    flags: (x) => x.underWeeks + x.overWeeks,
    billable: (x) => fin(x.billableShare),
    projectShare: (x) => fin(x.projectShare),
    efficiency: (x) => (x.efficiency.tasks ? fin(x.efficiency.ratio) : null),
    punctuality: (x) => fin(x.punctuality),
    rejection: (x) => fin(x.rejectionRate),
  });
  const rows = useMemo(() => {
    const byName = [...(q.data?.rows ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    return sorting.apply(byName);
  }, [q.data, sorting]);
  const chosen = rows.filter((x) => selected.has(x.personId));
  const target = chosen.length ? chosen : rows;
  const exportExcel = async (personIds?: string[]) => {
    setExporting('xlsx');
    try {
      saveFile(await rpc('kpi.teamExport', { from: r.from, to: r.to, personIds }));
    } catch (e) {
      toast.error(e);
    } finally {
      setExporting(null);
    }
  };
  const exportReport = async (format: 'pdf' | 'html', list: TeamRow[]) => {
    setExporting(format);
    try {
      const details = await Promise.all(list.slice(0, REPORT_DETAILS).map((x) => rpc('kpi.person', { personId: x.personId, from: r.from, to: r.to })));
      setReport({ format, rows: list, details });
    } catch (e) {
      toast.error(e);
    } finally {
      setExporting(null);
    }
  };
  const allChecked = rows.length > 0 && rows.every((x) => selected.has(x.personId));

  if (!can('kpi.team.view')) return <EmptyState>{t('evaluation.noAccess')}</EmptyState>;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('evaluation.title')} subtitle={t('evaluation.subtitle')} actions={<RangeButtons r={r} />} />
      <Notice tone="warn">
        <ShieldAlert className="mr-1 inline size-4" />
        {t('kpi.confidentialNote')}
      </Notice>
      <div className="grid gap-3 md:grid-cols-3">
        <Signal tone="good" icon={<Sparkles className="size-4" />} title={t('evaluation.signalFastTitle')} text={t('evaluation.signalFastText', { percent: Math.round(EFFICIENCY_FAST * 100) })} />
        <Signal tone="warn" icon={<ChevronUp className="size-4" />} title={t('evaluation.signalOverTitle')} text={t('evaluation.signalOverText', { hours: h(q.data?.toleranceMinutes ?? 120, 0) })} />
        <Signal tone="bad" icon={<ChevronDown className="size-4" />} title={t('evaluation.signalUnderTitle')} text={t('evaluation.signalUnderText', { hours: h(q.data?.toleranceMinutes ?? 120, 0) })} />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {q.data && <span className="num text-xs text-graphite">{t('kpi.asOf', { from: d(q.data.since), to: d(q.data.asOf) })}</span>}
        <Button className="ml-auto" icon={<ListChecks className="size-4" />} onClick={() => setCriteriaOpen((v) => !v)} aria-pressed={criteriaOpen}>
          {t('evaluation.criteria.show')} ({CRITERIA.length - hidden.filter((k) => CRITERIA.some((c) => c.key === k)).length}/{CRITERIA.length})
        </Button>
        <span className="flex flex-wrap items-center gap-1" role="group" aria-label={t('evaluation.export.title')}>
          <span className="text-xs text-graphite">{chosen.length ? t('evaluation.export.chosen', { count: chosen.length }) : t('evaluation.export.all')}</span>
          <Button size="sm" icon={<Download className="size-3.5" />} loading={exporting === 'xlsx'} disabled={!rows.length} onClick={() => void exportExcel(chosen.length ? chosen.map((x) => x.personId) : undefined)}>
            Excel
          </Button>
          <Button size="sm" icon={<Printer className="size-3.5" />} loading={exporting === 'pdf'} disabled={!rows.length} onClick={() => void exportReport('pdf', target)}>
            PDF
          </Button>
          <Button size="sm" icon={<FileCode2 className="size-3.5" />} loading={exporting === 'html'} disabled={!rows.length} onClick={() => void exportReport('html', target)} title={t('evaluation.export.htmlHint')}>
            {t('evaluation.export.html')}
          </Button>
        </span>
      </div>
      <BasisPanel toleranceHours={h(q.data?.toleranceMinutes ?? 120, 0)} />
      {criteriaOpen && <CriteriaPanel hidden={hidden} onToggle={toggleCriterion} onAll={showAll} toleranceHours={h(q.data?.toleranceMinutes ?? 120, 0)} />}
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && (
        <div className="grid grid-cols-2 gap-4 rounded-lg border border-line bg-sheet p-4 md:grid-cols-5">
          <Stat label={t('kpi.capacity')} value={hh(q.data.totals.capacityMinutes, 0)} />
          <Stat label={t('kpi.booked')} value={hh(q.data.totals.bookedMinutes, 0)} hint={pct(q.data.totals.capacityMinutes ? q.data.totals.bookedMinutes / q.data.totals.capacityMinutes : Number.NaN)} />
          <Stat label={t('kpi.billableShare')} value={pct(q.data.totals.bookedMinutes ? q.data.totals.billableMinutes / q.data.totals.bookedMinutes : Number.NaN)} />
          <Stat label={t('kpi.efficiency')} value={pct(q.data.totals.efficiencyRatio)} hint={t('cockpit.teamEfficiencyHint')} />
          <Stat label={t('cockpit.weekFlags')} value={`${q.data.totals.underWeeks} / ${q.data.totals.overWeeks}`} hint={t('cockpit.weekFlagsHint', { hours: h(q.data.toleranceMinutes, 0) })} />
        </div>
      )}
      {q.data && q.data.totals.unplannedMinutes > 0 && (
        // finished tasks without plan hours are not in the ratio: say how much of the work it covers (request 19)
        <Notice tone={q.data.totals.efficiencyCoverage < PLAN_COVERAGE_LOW ? 'warn' : 'neutral'}>
          {q.data.totals.efficiencyCoverage < PLAN_COVERAGE_LOW
            ? t('hourTypes.coverageNotice', { pct: pct(q.data.totals.efficiencyCoverage), hours: h(q.data.totals.unplannedMinutes, 1) })
            : t('hourTypes.coverageInfo', { hours: h(q.data.totals.unplannedMinutes, 1) })}
        </Notice>
      )}
      {rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Checkbox label={t('common.selectAll')} checked={allChecked} onChange={() => setSelected(allChecked ? new Set() : new Set(rows.map((x) => x.personId)))} />
          <span className="text-xs text-graphite">{t('evaluation.selectHint')}</span>
        </div>
      )}
      {rows.length > 0 && (
        <ScrollTable label={t('nav.evaluation')}>
          {hiddenCols.length > 0 && <style>{hiddenCols.map((c) => `.eval-table th:nth-child(${c}),.eval-table td:nth-child(${c}){display:none}`).join('')}</style>}
          <table className="eval-table w-full min-w-[1320px] text-sm">
            <thead className="border-b border-line bg-sheet-2">
              <tr>
                <SortTh label={t('common.person')} sortKey="name" sort={sorting.sort} onSort={sorting.toggle} className="min-w-64" />
                <SortTh label={t('kpi.capacity')} sortKey="capacity" sort={sorting.sort} onSort={sorting.toggle} right />
                <SortTh label={t('kpi.booked')} sortKey="booked" sort={sorting.sort} onSort={sorting.toggle} right />
                <SortTh
                  label={
                    <span title={t('evaluation.balanceHint')} className="inline-flex items-center gap-1">
                      {t('evaluation.balanceCol')}
                      <Info className="size-3 text-faint" aria-hidden />
                    </span>
                  }
                  sortKey="balance"
                  sort={sorting.sort}
                  onSort={sorting.toggle}
                  right
                />
                <SortTh label={t('cockpit.weekFlagsCol')} sortKey="flags" sort={sorting.sort} onSort={sorting.toggle} />
                <SortTh label={t('kpi.billableShare')} sortKey="billable" sort={sorting.sort} onSort={sorting.toggle} right />
                <SortTh label={<span title={t('areas.shareHint')}>{t('kpi.projectShare')}</span>} sortKey="projectShare" sort={sorting.sort} onSort={sorting.toggle} right />
                <SortTh label={t('kpi.efficiency')} sortKey="efficiency" sort={sorting.sort} onSort={sorting.toggle} right />
                <SortTh label={t('kpi.punctuality')} sortKey="punctuality" sort={sorting.sort} onSort={sorting.toggle} right />
                <SortTh label={t('kpi.rejectionRate')} sortKey="rejection" sort={sorting.sort} onSort={sorting.toggle} right />
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => (
                <tr key={x.personId} className={clsx('cursor-pointer border-b border-line/70 hover:bg-sheet-2', selected.has(x.personId) && 'bg-accent-soft/40')} onClick={() => setPerson({ id: x.personId, name: x.name })}>
                  <td className={td}>
                    <div className="flex items-center gap-2">
                      <span onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          label={<span className="sr-only">{t('common.select')}</span>}
                          checked={selected.has(x.personId)}
                          onChange={(e) =>
                            setSelected((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(x.personId);
                              else next.delete(x.personId);
                              return next;
                            })
                          }
                        />
                      </span>
                      <Avatar personId={x.personId} name={x.name} avatarAt={avatars.get(x.personId) ?? null} />
                      <div>
                        <div className="font-medium">{x.name}</div>
                        <div className="text-xs text-graphite">{x.discipline ?? ''}</div>
                      </div>
                    </div>
                  </td>
                  <td className={clsx(td, 'num text-right')}>{h(x.capacityMinutes, 1)}</td>
                  <td className={clsx(td, 'num text-right')}>{h(x.bookedMinutes, 1)}</td>
                  <td className={clsx(td, 'num text-right', x.balanceMinutes < 0 ? 'text-bad' : x.balanceMinutes > 0 ? 'text-warn' : '')}>
                    {x.balanceMinutes > 0 ? '+' : ''}
                    {h(x.balanceMinutes, 1)}
                  </td>
                  <td className={td}>
                    <span className="flex flex-wrap gap-1">
                      {x.underWeeks > 0 && (
                        <Pill tone="bad" title={t('kpi.underLegend')}>
                          <ChevronDown className="size-3" />
                          {x.underWeeks}
                        </Pill>
                      )}
                      {x.overWeeks > 0 && (
                        <Pill tone="warn" title={t('kpi.overLegend')}>
                          <ChevronUp className="size-3" />
                          {x.overWeeks}
                        </Pill>
                      )}
                      {!x.underWeeks && !x.overWeeks && <span className="text-xs text-graphite">{t('cockpit.noFlags', { count: x.finishedWeeks })}</span>}
                    </span>
                  </td>
                  <td className={clsx(td, 'num text-right')} title={hourTypesTitle(x.hourTypes)}>
                    {pct(x.billableShare)}
                    {x.hourTypes.nonBillableProject > 0 && (
                      <span className="block text-xs text-graphite">{t('hourTypes.nonBillableProject')}: {h(x.hourTypes.nonBillableProject, 1)} h</span>
                    )}
                  </td>
                  <td className={clsx(td, 'num text-right')}>
                    {pct(x.projectShare)}
                    {x.internalMinutes > 0 && <span className="block text-xs text-graphite">{t('areas.internalHours', { hours: h(x.internalMinutes, 1) })}</span>}
                  </td>
                  <td className={clsx(td, 'num text-right')} title={coverageText(x.efficiency) || undefined}>
                    {x.efficiency.tasks ? (
                      <span title={[t('kpi.efficiencyHint', { count: x.efficiency.tasks, plan: h(x.efficiency.earnedMinutes, 1), actual: h(x.efficiency.actualMinutes, 1) }), coverageText(x.efficiency)].filter(Boolean).join('\n')}>
                        <span className={clsx('font-semibold', efficiencyTone(x.efficiency.ratio) === 'good' ? 'text-good' : efficiencyTone(x.efficiency.ratio) === 'warn' ? 'text-warn' : '')}>{pct(x.efficiency.ratio)}</span>
                        <span className="block text-xs text-graphite">
                          {t('cockpit.tasksCount', { count: x.efficiency.tasks })}
                          {x.efficiency.fastTasks > 0 && (
                            <span className="ml-1 inline-flex items-center gap-0.5 text-good">
                              <Sparkles className="size-3" />
                              {x.efficiency.fastTasks}
                            </span>
                          )}
                        </span>
                      </span>
                    ) : (
                      '-'
                    )}
                    {x.efficiency.unplannedMinutes > 0 && (
                      <span className={clsx('block text-xs', coverageLow(x.efficiency) ? 'text-warn' : 'text-graphite')}>{t('hourTypes.coverageShort', { hours: h(x.efficiency.unplannedMinutes, 1) })}</span>
                    )}
                  </td>
                  <td className={clsx(td, 'num text-right')}>{Number.isFinite(x.punctuality) ? `${x.weeksOnTime}/${x.weeksDue}` : '-'}</td>
                  <td className={clsx(td, 'num text-right')}>{pct(x.rejectionRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollTable>
      )}
      <p className="text-xs text-graphite">{t('evaluation.hint')}</p>
      <HourTerms />
      <PersonDetail
        person={person}
        from={r.from}
        to={r.to}
        onClose={() => setPerson(null)}
        busy={exporting}
        onExport={(format) => {
          const row = rows.find((x) => x.personId === person?.id);
          if (!row) return;
          if (format === 'xlsx') void exportExcel([row.personId]);
          else void exportReport(format, [row]);
        }}
      />
      {report && report.format === 'pdf' && (
        <ReportHost open onClose={() => setReport(null)} title={t('evaluation.export.reportTitle')}>
          <EvaluationReport job={report} from={r.from} to={r.to} />
        </ReportHost>
      )}
      {report && report.format === 'html' && (
        <HtmlReportHost
          open
          onDone={() => setReport(null)}
          title={t('evaluation.export.reportTitle')}
          fileName={`Mitarbeiterauswertung_${report.rows.length === 1 ? report.rows[0].name.replace(/[^\p{L}\p{N}]+/gu, '_') : `${report.rows.length}_Personen`}_${r.from}_${r.to}.html`}
        >
          <EvaluationReport job={report} from={r.from} to={r.to} />
        </HtmlReportHost>
      )}
    </div>
  );
}

/**
 * What the evaluation rests on and how "highest" and "lowest" are meant (round 5, request 17): there is no overall score
 * and no ranking; each column is its own criterion, sorted by a click on its head.
 */
function BasisPanel({ toleranceHours }: { toleranceHours: string }) {
  const { t } = useTranslation();
  return (
    <details className="rounded-lg border border-line bg-sheet px-3 py-2 text-sm">
      <summary className="cursor-pointer font-medium select-none">
        <Info className="mr-1 inline size-4 text-accent" aria-hidden />
        {t('evaluation.basis.title')}
      </summary>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-graphite">
        <li>{t('evaluation.basis.data')}</li>
        <li>{t('evaluation.basis.target')}</li>
        <li>{t('evaluation.basis.balance')}</li>
        <li>{t('evaluation.basis.noScore')}</li>
        <li>{t('evaluation.basis.extremes', { hours: toleranceHours })}</li>
        <li>{t('evaluation.basis.context')}</li>
      </ul>
    </details>
  );
}

/** The report of the chosen persons: the table and, for the first persons, their weeks and projects. */
function EvaluationReport({ job, from, to }: { job: ReportJob; from: string; to: string }) {
  const { t } = useTranslation();
  const { me } = useMe();
  return (
    <div className="flex flex-col gap-4 text-ink">
      <ReportHeader title={t('evaluation.export.reportTitle')} sub={`${t('common.period')}: ${d(from)} – ${d(to)}`} company={me.settings.companyName} />
      <p className="text-xs font-semibold">{t('kpi.confidentialNote')}</p>
      <table className="w-full text-xs">
        <thead className="border-b border-line">
          <tr>
            <th className={th}>{t('common.person')}</th>
            <th className={clsx(th, 'text-right')}>{t('kpi.capacity')}</th>
            <th className={clsx(th, 'text-right')}>{t('kpi.booked')}</th>
            <th className={clsx(th, 'text-right')}>{t('evaluation.balanceCol')}</th>
            <th className={clsx(th, 'text-right')}>{t('cockpit.weekFlagsCol')}</th>
            <th className={clsx(th, 'text-right')}>{t('kpi.billableShare')}</th>
            <th className={clsx(th, 'text-right')}>{t('kpi.projectShare')}</th>
            <th className={clsx(th, 'text-right')}>{t('kpi.efficiency')}</th>
            <th className={clsx(th, 'text-right')}>{t('kpi.punctuality')}</th>
            <th className={clsx(th, 'text-right')}>{t('kpi.rejectionRate')}</th>
          </tr>
        </thead>
        <tbody>
          {job.rows.map((x) => (
            <tr key={x.personId} className="border-b border-line/60">
              <td className={td} data-tip={x.discipline ?? undefined}>
                {x.name}
              </td>
              <td className={clsx(td, 'num text-right')}>{h(x.capacityMinutes, 1)}</td>
              <td className={clsx(td, 'num text-right')}>{h(x.bookedMinutes, 1)}</td>
              <td className={clsx(td, 'num text-right')} data-tip={t('evaluation.balanceHint')}>
                {x.balanceMinutes > 0 ? '+' : ''}
                {h(x.balanceMinutes, 1)}
              </td>
              <td className={clsx(td, 'num text-right')} data-tip={`${t('kpi.underLegend')}: ${x.underWeeks} · ${t('kpi.overLegend')}: ${x.overWeeks}`}>
                {x.underWeeks} / {x.overWeeks}
              </td>
              <td className={clsx(td, 'num text-right')}>{pct(x.billableShare)}</td>
              <td className={clsx(td, 'num text-right')}>{pct(x.projectShare)}</td>
              <td className={clsx(td, 'num text-right')}>{x.efficiency.tasks ? pct(x.efficiency.ratio) : '-'}</td>
              <td className={clsx(td, 'num text-right')}>{Number.isFinite(x.punctuality) ? `${x.weeksOnTime}/${x.weeksDue}` : '-'}</td>
              <td className={clsx(td, 'num text-right')}>{pct(x.rejectionRate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {job.rows.length > job.details.length && <p className="text-xs text-graphite">{t('evaluation.export.detailsLimited', { count: job.details.length })}</p>}
      {job.details.map((p) => (
        <section key={p.person.id} className="break-inside-avoid border-t border-line pt-3">
          <h2 className="text-sm font-semibold">{p.person.name}</h2>
          <p className="mb-2 text-xs text-graphite">
            {t('kpi.booked')} {hh(p.weeks.reduce((s, w) => s + w.bookedMinutes, 0), 1)} · {t('kpi.capacity')} {hh(p.weeks.reduce((s, w) => s + w.capacityMinutes, 0), 1)} · {t('kpi.efficiency')}{' '}
            {p.efficiency.tasks ? pct(p.efficiency.ratio) : '-'}
          </p>
          <WeekBars
            data={p.weeks.map((w) => {
              const n = isoWeek(w.weekStart).week;
              return { label: `${t('kpi.weekPrefix')} ${n}`, axis: `${n}`, booked: w.bookedMinutes, billable: w.billableMinutes, capacity: w.capacityMinutes, comp: w.compensationMinutes, state: w.state };
            })}
          />
          <HBars head={t('common.project')} rows={p.byProject.map((x) => ({ label: `${x.number} ${x.name}`, value: x.minutes, billable: x.billable }))} />
        </section>
      ))}
      <p className="text-[11px] text-graphite">{t('evaluation.hint')}</p>
    </div>
  );
}

/** What each figure on the page means: the basis for explaining the evaluation to the works council and to the people evaluated. */
function CriteriaPanel({ hidden, onToggle, onAll, toleranceHours }: { hidden: string[]; onToggle: (key: string) => void; onAll: () => void; toleranceHours: string }) {
  const { t } = useTranslation();
  const { me } = useMe();
  const vars = { hours: toleranceHours, good: Math.round(EFFICIENCY_GOOD * 100), low: Math.round(EFFICIENCY_LOW * 100), days: me.settings.submitDeadlineDays ?? 1 };
  return (
    <Panel
      title={t('evaluation.criteria.title')}
      actions={
        <Button size="sm" variant="ghost" onClick={onAll}>
          {t('evaluation.criteria.all')}
        </Button>
      }
    >
      <p className="mb-3 text-sm text-graphite">{t('evaluation.criteria.intro')}</p>
      <ul className="grid gap-3 md:grid-cols-2">
        {CRITERIA.map((c) => (
          <li key={c.key}>
            <Checkbox
              label={<span className="font-medium">{t(`evaluation.criteria.${c.key}.name`)}</span>}
              hint={t(`evaluation.criteria.${c.key}.desc`, vars)}
              checked={!hidden.includes(c.key)}
              onChange={() => onToggle(c.key)}
            />
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-graphite">{t('evaluation.criteria.thresholds', vars)}</p>
    </Panel>
  );
}

function Signal({ tone, icon, title, text }: { tone: 'good' | 'warn' | 'bad'; icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className={clsx('flex gap-2 rounded-lg border px-3 py-2 text-sm', tone === 'good' ? 'border-good/30 bg-good-soft' : tone === 'warn' ? 'border-warn/30 bg-warn-soft' : 'border-bad/30 bg-bad-soft')}>
      <span className={clsx('mt-0.5', tone === 'good' ? 'text-good' : tone === 'warn' ? 'text-warn' : 'text-bad')}>{icon}</span>
      <span>
        <span className="block font-semibold">{title}</span>
        <span className="text-xs text-graphite">{text}</span>
      </span>
    </div>
  );
}

function PersonDetail({
  person,
  from,
  to,
  onClose,
  onExport,
  busy,
}: {
  person: { id: string; name: string } | null;
  from: string;
  to: string;
  onClose: () => void;
  onExport: (format: 'xlsx' | 'pdf' | 'html') => void;
  busy: string | null;
}) {
  const { t } = useTranslation();
  const coverageText = useCoverageText();
  const q = useQ('kpi.person', { personId: person?.id ?? '', from, to }, { enabled: !!person });
  return (
    <Modal
      open={!!person}
      onClose={onClose}
      wide
      title={person ? t('cockpit.personTitle', { name: person.name }) : ''}
      footer={
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-graphite">{t('evaluation.export.person')}</span>
          <Button size="sm" icon={<Download className="size-3.5" />} loading={busy === 'xlsx'} onClick={() => onExport('xlsx')}>
            Excel
          </Button>
          <Button size="sm" icon={<Printer className="size-3.5" />} loading={busy === 'pdf'} onClick={() => onExport('pdf')}>
            PDF
          </Button>
          <Button size="sm" icon={<FileCode2 className="size-3.5" />} loading={busy === 'html'} onClick={() => onExport('html')}>
            {t('evaluation.export.html')}
          </Button>
        </span>
      }
    >
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && (
        <div className="flex flex-col gap-5">
          <Notice tone="warn">
            <ShieldAlert className="mr-1 inline size-4" />
            {t('kpi.confidentialNote')}
          </Notice>
          {q.data.efficiency.unplannedMinutes > 0 && <Notice tone={coverageLow(q.data.efficiency) ? 'warn' : 'neutral'}>{coverageText(q.data.efficiency)}</Notice>}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            <Stat
              label={t('kpi.efficiency')}
              value={q.data.efficiency.tasks ? pct(q.data.efficiency.ratio) : '-'}
              hint={q.data.efficiency.tasks ? t('kpi.efficiencyHint', { count: q.data.efficiency.tasks, plan: h(q.data.efficiency.earnedMinutes, 1), actual: h(q.data.efficiency.actualMinutes, 1) }) : t('kpi.efficiencyNone')}
            />
            <Stat
              label={t('kpi.solo')}
              value={q.data.efficiency.soloTasks ? pct(q.data.efficiency.soloRatio) : '-'}
              hint={t('kpi.soloHint', { count: q.data.efficiency.soloTasks })}
            />
            <Stat label={t('cockpit.saved')} value={q.data.efficiency.tasks ? `${q.data.efficiency.savedMinutes > 0 ? '+' : ''}${hh(q.data.efficiency.savedMinutes, 1)}` : '-'} hint={t('cockpit.savedHint')} />
            <Stat label={t('kpi.booked')} value={hh(q.data.weeks.reduce((s, w) => s + w.bookedMinutes, 0), 1)} />
            <Stat label={t('kpi.capacity')} value={hh(q.data.weeks.reduce((s, w) => s + (w.capacityMinutes ?? 0), 0), 1)} />
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold">{t('kpi.weekly')}</h3>
            <WeekBars
              data={q.data.weeks.map((w) => {
                const n = isoWeek(w.weekStart).week;
                return {
                  label: `${t('kpi.weekPrefix')} ${n}`,
                  axis: `${n}`,
                  booked: w.bookedMinutes,
                  billable: w.billableMinutes,
                  capacity: w.capacityMinutes,
                  comp: w.compensationMinutes,
                  state: w.state,
                  note: w.compensationMinutes ? t('week.compHint', { hours: h(w.compensationMinutes) }) : undefined,
                };
              })}
            />
          </div>
          <div>
            <h3 className="mb-1 text-sm font-semibold">{t('overview.planVsActual')}</h3>
            <p className="mb-2 text-xs text-graphite">{t('kpi.efficiencyExplain')}</p>
            {q.data.efficiency.items.length ? <EfficiencyTable items={q.data.efficiency.items} /> : <p className="text-sm text-graphite">{t('kpi.efficiencyNone')}</p>}
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold">{t('kpi.byProject')}</h3>
            <HBars head={t('common.project')} rows={q.data.byProject.map((p) => ({ label: `${p.number} ${p.name}`, value: p.minutes, billable: p.billable }))} />
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold">{t('hourTypes.title')}</h3>
            <HourTypeBars types={q.data.hourTypes} />
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold">{t('areas.title')}</h3>
            <AreaBars areas={q.data.areas} />
          </div>
          <HourTerms />
        </div>
      )}
    </Modal>
  );
}
